/**
 * GoogleDriveContainer.tsx
 *
 * Renders the patient's legacy Google Drive folder below the EFS file browser.
 * Lets staff preview Drive files inline and SYNC selected files (copy) into the
 * patient's EFS documents folder, preserving subfolder structure.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Folder,
  HardDrive,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';
import {
  listDriveFiles,
  fetchDriveFileViewUrl,
  downloadDriveFile,
  syncDriveFiles,
  type DriveNode,
} from '@/services/patient-drive.service';
import { getErrorMessage } from '@/services/apiClient';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui';
import { useToast } from '@/hooks/useToast';

interface GoogleDriveContainerProps {
  patientId: number;
  /** Called after a successful sync so the EFS list can refresh. */
  onSynced?: () => void;
}

/** Folder portion of a Drive node's relative path (path minus the file name). */
function parentFolder(path: string): string {
  const parts = path.split('/');
  parts.pop();
  return parts.join('/');
}

/** Flatten the tree to just files (folders can't be synced directly). */
function collectFiles(nodes: DriveNode[], acc: DriveNode[] = []): DriveNode[] {
  for (const n of nodes) {
    if (n.isFolder) collectFiles(n.children ?? [], acc);
    else acc.push(n);
  }
  return acc;
}

/** All file IDs nested anywhere under a node (the node itself if it's a file). */
function descendantFileIds(node: DriveNode): string[] {
  if (!node.isFolder) return [node.id];
  const ids: string[] = [];
  for (const c of node.children ?? []) ids.push(...descendantFileIds(c));
  return ids;
}

/** Checkbox that supports the indeterminate (partial) visual state. */
function TriCheckbox({ checked, indeterminate, disabled, onChange, className }: {
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={className}
    />
  );
}

export function GoogleDriveContainer({ patientId, onSynced }: GoogleDriveContainerProps) {
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [preview, setPreview] = useState<DriveNode | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['patient-drive', patientId],
    queryFn: () => listDriveFiles(patientId),
    staleTime: 60_000,
  });

  const allFiles = useMemo(() => collectFiles(data?.files ?? []), [data]);
  const fileById = useMemo(() => {
    const m = new Map<string, DriveNode>();
    for (const f of allFiles) m.set(f.id, f);
    return m;
  }, [allFiles]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleFolder = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }, []);

  // Select/deselect every file nested under a folder (any depth). Migrating a
  // folder copies its whole structure, since each file carries its own path.
  const toggleFolderSelect = useCallback((node: DriveNode) => {
    const ids = descendantFileIds(node);
    if (ids.length === 0) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const allIn = ids.every((id) => next.has(id));
      if (allIn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const allSelected = allFiles.length > 0 && selected.size === allFiles.length;
  const toggleSelectAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(allFiles.map((f) => f.id)));
  }, [allSelected, allFiles]);

  async function handleSync() {
    if (selected.size === 0) return;
    setSyncing(true);
    try {
      const files = [...selected].map((id) => ({ id, folder: parentFolder(fileById.get(id)?.path ?? '') }));
      const res = await syncDriveFiles(patientId, files);
      if (res.synced === res.total) {
        toast.success(`Synced ${res.synced} file${res.synced === 1 ? '' : 's'} to patient files.`);
      } else {
        toast.warning(`Synced ${res.synced} of ${res.total}. ${res.total - res.synced} failed.`);
      }
      setSelected(new Set());
      onSynced?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Sync failed.'));
    } finally {
      setSyncing(false);
    }
  }

  // Only show the container when the account is connected, the patient has a
  // Drive folder, and that folder actually contains files.
  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner size="sm" /> Checking Google Drive…
        </div>
      </div>
    );
  }
  if (!data?.connected || !data.hasFolder || allFiles.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-slate-900">Google Drive</h3>
          <span className="text-xs text-slate-500">({allFiles.length} file{allFiles.length === 1 ? '' : 's'})</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="rounded border-slate-300" />
            Select all
          </label>
          <Button size="sm" onClick={() => void handleSync()} disabled={selected.size === 0 || syncing}>
            {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Sync{selected.size > 0 ? ` (${selected.size})` : ''}
          </Button>
        </div>
      </div>

      <div className="p-2 max-h-80 overflow-y-auto">
        <DriveTree
          nodes={data.files}
          depth={0}
          expanded={expanded}
          selected={selected}
          onToggleFolder={toggleFolder}
          onToggleSelect={toggleSelect}
          onToggleFolderSelect={toggleFolderSelect}
          onPreview={setPreview}
        />
      </div>

      <p className="border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400">
        Syncing copies files into the patient's Files (originals stay in Google Drive).
      </p>

      {preview && (
        <DrivePreviewModal
          patientId={patientId}
          node={preview}
          onClose={() => setPreview(null)}
          onDownload={() => void downloadDriveFile(patientId, preview.id, preview.name)}
        />
      )}
    </div>
  );
}

interface DriveTreeProps {
  nodes: DriveNode[];
  depth: number;
  expanded: Set<string>;
  selected: Set<string>;
  onToggleFolder: (path: string) => void;
  onToggleSelect: (id: string) => void;
  onToggleFolderSelect: (node: DriveNode) => void;
  onPreview: (node: DriveNode) => void;
}

function DriveTree({ nodes, depth, expanded, selected, onToggleFolder, onToggleSelect, onToggleFolderSelect, onPreview }: DriveTreeProps) {
  return (
    <ul className="space-y-0.5">
      {nodes.map((node) => {
        const isOpen = expanded.has(node.path);
        const descIds = node.isFolder ? descendantFileIds(node) : [];
        const folderChecked = descIds.length > 0 && descIds.every((id) => selected.has(id));
        const folderPartial = !folderChecked && descIds.some((id) => selected.has(id));
        return (
          <li key={node.id}>
            <div
              className="flex items-center gap-2 rounded px-2 py-1 hover:bg-slate-50"
              style={{ paddingLeft: `${depth * 16 + 8}px` }}
            >
              {node.isFolder ? (
                <>
                  <TriCheckbox
                    checked={folderChecked}
                    indeterminate={folderPartial}
                    disabled={descIds.length === 0}
                    onChange={() => onToggleFolderSelect(node)}
                    className="rounded border-slate-300"
                  />
                  <button
                    type="button"
                    onClick={() => onToggleFolder(node.path)}
                    className="inline-flex items-center text-slate-400 hover:text-slate-600"
                  >
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700 truncate">{node.name}</span>
                </>
              ) : (
                <>
                  <input
                    type="checkbox"
                    checked={selected.has(node.id)}
                    onChange={() => onToggleSelect(node.id)}
                    className="rounded border-slate-300"
                  />
                  <span className="h-4 w-4 flex-shrink-0" />
                  <button
                    type="button"
                    onClick={() => onPreview(node)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    title="Open preview"
                  >
                    {node.iconLink ? (
                      <img src={node.iconLink} alt="" className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <span className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className="text-sm text-blue-600 hover:underline truncate">{node.name}</span>
                  </button>
                </>
              )}
            </div>
            {node.isFolder && isOpen && node.children && node.children.length > 0 && (
              <DriveTree
                nodes={node.children}
                depth={depth + 1}
                expanded={expanded}
                selected={selected}
                onToggleFolder={onToggleFolder}
                onToggleSelect={onToggleSelect}
                onToggleFolderSelect={onToggleFolderSelect}
                onPreview={onPreview}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface DrivePreviewModalProps {
  patientId: number;
  node: DriveNode;
  onClose: () => void;
  onDownload: () => void;
}

function DrivePreviewModal({ patientId, node, onClose, onDownload }: DrivePreviewModalProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objUrl: string | null = null;
    setLoading(true);
    fetchDriveFileViewUrl(patientId, node.id).then((u) => {
      if (cancelled) { if (u) URL.revokeObjectURL(u); return; }
      objUrl = u;
      setUrl(u);
      setLoading(false);
    });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [patientId, node.id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [onClose]);

  const isPdf = node.mimeType === 'application/pdf'
    || node.mimeType === 'application/vnd.google-apps.document'
    || node.mimeType === 'application/vnd.google-apps.presentation';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 flex-shrink-0">
          <h3 className="text-sm font-semibold text-slate-900 truncate">{node.name}</h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={onDownload}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Download
            </Button>
            <button type="button" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4 min-h-[300px]">
          {loading ? (
            <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
          ) : url && node.isImage ? (
            <img src={url} alt={node.name} className="max-w-full max-h-[75vh] object-contain rounded shadow-lg" />
          ) : url && isPdf ? (
            <iframe src={url} title={node.name} className="w-full h-[75vh] rounded border border-slate-200" />
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-500">Preview not available for this file type.</p>
              <Button onClick={onDownload}>
                <Download className="h-4 w-4 mr-1" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
